import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import type { LatLngExpression } from "leaflet";
import { MapPinPlus } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import MapThumbnailCaller from "@/components/pages/search/map/components/MapThumbnailCaller";
import { cn } from "@/lib/utils";
type Props = {
  title: string;
  locationName?: ReactNode;
  /** A point location (pin). Mutually exclusive with `polygon`. */
  lat?: string | null;
  lng?: string | null;
  /** An area outline (rendered blue), e.g. for demand. Takes precedence. */
  polygon?: LatLngExpression[][];
  onMapClick?: () => void;
  onClick?: () => void;
  className?: string;
  mapClassName?: string;
  /** Render flat (no Card chrome), e.g. the mobile detail layout. */
  bare?: boolean;
  onAdd?: () => void;
  addLabel?: string;
  emptyTitle?: string;
  emptyDescription?: string;
};
export function LocationCard(t0) {
  const $ = _c(29);
  const {
    title,
    locationName,
    lat,
    lng,
    polygon,
    onMapClick,
    onClick,
    className,
    mapClassName,
    bare,
    onAdd,
    addLabel,
    emptyTitle,
    emptyDescription
  } = t0;
  const hasPolygon = !!polygon && polygon.length > 0;
  const hasCoords = !!lat && !!lng;
  if (!hasPolygon && !hasCoords && !onAdd) {
    return null;
  }
  const isEmpty = !hasPolygon && !hasCoords;
  let t1;
  if ($[0] !== addLabel || $[1] !== emptyDescription || $[2] !== emptyTitle || $[3] !== isEmpty || $[4] !== lat || $[5] !== lng || $[6] !== locationName || $[7] !== mapClassName || $[8] !== onAdd || $[9] !== onMapClick || $[10] !== polygon || $[11] !== title) {
    t1 = isEmpty ? <div className="flex flex-col items-center justify-center gap-3 py-6 text-center"><div className="flex h-11 w-11 items-center justify-center rounded-full bg-muted"><MapPinPlus className="h-5 w-5 text-faint-foreground" /></div><div className="space-y-1"><p className="text-sm font-semibold text-foreground">{emptyTitle ?? title}</p>{emptyDescription && <p className="mx-auto max-w-sm text-xs text-muted-foreground">{emptyDescription}</p>}</div>{addLabel && <Button variant="outline" size="sm" onClick={e => {
        e.stopPropagation();
        onAdd?.();
      }}>{addLabel}</Button>}</div> : <div className="flex flex-col gap-0.5"><p className="text-lg font-semibold text-foreground-heading">{title}</p>{locationName && <span className="text-sm text-muted-foreground">{locationName}</span>}<div onClick={onMapClick} className={cn("mt-3", onMapClick && "cursor-pointer")}><MapThumbnailCaller lat={polygon ? undefined : lat ?? undefined} lng={polygon ? undefined : lng ?? undefined} polygonPrimary={polygon} emptyBgClassName="bg-muted" className={cn("h-48 w-full rounded-lg shadow-xs", mapClassName)} /></div></div>;
    $[0] = addLabel;
    $[1] = emptyDescription;
    $[2] = emptyTitle;
    $[3] = isEmpty;
    $[4] = lat;
    $[5] = lng;
    $[6] = locationName;
    $[7] = mapClassName;
    $[8] = onAdd;
    $[9] = onMapClick;
    $[10] = polygon;
    $[11] = title;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  const inner = t1;
  const handleClick = isEmpty ? undefined : onClick;
  if (bare) {
    const t2 = handleClick && "cursor-pointer";
    let t3;
    if ($[13] !== className || $[14] !== t2) {
      t3 = cn("p-4", t2, className);
      $[13] = className;
      $[14] = t2;
      $[15] = t3;
    } else {
      t3 = $[15];
    }
    let t4;
    if ($[16] !== handleClick || $[17] !== inner || $[18] !== t3) {
      t4 = <div onClick={handleClick} className={t3}>{inner}</div>;
      $[16] = handleClick;
      $[17] = inner;
      $[18] = t3;
      $[19] = t4;
    } else {
      t4 = $[19];
    }
    return t4;
  }
  const t2 = handleClick && "cursor-pointer transition-shadow hover:shadow-md";
  let t3;
  if ($[20] !== className || $[21] !== t2) {
    t3 = cn(t2, className);
    $[20] = className;
    $[21] = t2;
    $[22] = t3;
  } else {
    t3 = $[22];
  }
  let t4;
  if ($[23] !== inner) {
    t4 = <CardContent className="p-5" data-sentry-element="CardContent" data-sentry-source-file="LocationCard.tsx">{inner}</CardContent>;
    $[23] = inner;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  let t5;
  if ($[25] !== handleClick || $[26] !== t3 || $[27] !== t4) {
    t5 = <Card onClick={handleClick} className={t3} data-sentry-element="Card" data-sentry-component="LocationCard" data-sentry-source-file="LocationCard.tsx">{t4}</Card>;
    $[25] = handleClick;
    $[26] = t3;
    $[27] = t4;
    $[28] = t5;
  } else {
    t5 = $[28];
  }
  return t5;
}